import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ShieldCheck } from 'lucide-react';
import { useLanguage } from './LanguageContext';
import { t } from '../utils/translations';
export type InfoPageKey = 'terms' | 'privacy' | 'cancellation' | 'insurance' | 'about' | 'fleet' | 'coverageArea' | 'careers' | 'contact' | 'residentialMoving' | 'commercialMoving' | 'furnitureAssembly' | 'tvProtection' | 'packing' | 'sameWeek';
export function InfoModal({
  pageKey,
  onClose
}: {
  pageKey: InfoPageKey | null;
  onClose: () => void;
}) {
  const {
    lang
  } = useLanguage();
  const info = t[lang].infoPages;
  useEffect(() => {
    if (pageKey) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [pageKey]);
  // ESC to close
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (pageKey) window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [pageKey, onClose]);
  const rawPage: any = pageKey ? info.pages?.[pageKey] ?? (info as any)[pageKey] : null;
  const page: any = rawPage ? {
    title: rawPage.title,
    intro: rawPage.intro,
    sections: (rawPage.sections || []).map((s: any) => ({
      h: s.h ?? s.heading,
      p: s.p ?? s.body
    }))
  } : null;
  const closeLabel = info.closeLabel ?? (info as any).closeBtn ?? 'Close';
  return <AnimatePresence data-id="element-380">
      {pageKey && page && <motion.div className="fixed inset-0 z-[110] flex items-center justify-center p-2 sm:p-4" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} data-id="element-381">
          <motion.div className="absolute inset-0 bg-black/80 backdrop-blur-sm" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} data-id="element-382" />

          <motion.div initial={{
        opacity: 0,
        scale: 0.96,
        y: 30
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.96,
        y: 30
      }} transition={{
        type: 'spring',
        stiffness: 200,
        damping: 22
      }} className="relative w-full max-w-3xl max-h-[92vh] bg-white rounded-3xl shadow-2xl overflow-hidden flex flex-col" data-id="element-383">
            {/* Header */}
            <div className="bg-mf-black text-white px-6 md:px-10 py-6 relative flex-shrink-0" data-id="element-384">
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[100px] bg-mf-red/20 blur-[80px] rounded-full pointer-events-none" data-id="element-385"></div>
              <button onClick={onClose} className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors z-10" aria-label={closeLabel} data-id="element-386">
                <X size={20} data-id="element-387" />
              </button>
              <div className="relative z-10 pr-12" data-id="element-388">
                <h2 className="text-2xl md:text-4xl font-condensed font-black uppercase tracking-tight" data-id="element-389">
                  {page.title}
                </h2>
                <p className="text-xs text-gray-400 mt-2 flex items-center" data-id="element-390">
                  <ShieldCheck size={12} className="mr-2 text-mf-red" data-id="element-391" />
                  {info.lastUpdated}
                </p>
              </div>
            </div>

            {/* Body (scrollable) */}
            <div className="flex-1 overflow-y-auto px-6 md:px-10 py-8" data-id="element-392">
              {page.intro && <p className="text-gray-700 text-base md:text-lg leading-relaxed mb-8 pb-6 border-b border-gray-200" data-id="element-393">
                  {page.intro}
                </p>}
              <div className="space-y-7" data-id="element-394">
                {page.sections.map((s: any, i: number) => <section key={i} data-id="element-395">
                    <h3 className="text-base font-condensed font-black uppercase tracking-wider text-mf-black mb-2 flex items-center" data-id="element-396">
                      <span className="w-1 h-5 bg-mf-red rounded-full mr-3" data-id="element-397"></span>
                      {s.h}
                    </h3>
                    <p className="text-gray-700 leading-relaxed text-sm md:text-base ml-4" data-id="element-398">
                      {s.p}
                    </p>
                  </section>)}
              </div>
            </div>

            {/* Footer close */}
            <div className="border-t border-gray-200 bg-gray-50 px-6 md:px-10 py-4 flex justify-end flex-shrink-0" data-id="element-399">
              <button onClick={onClose} className="px-6 py-2.5 rounded-xl bg-mf-black hover:bg-gray-800 text-white font-condensed font-black uppercase tracking-wider text-sm transition-colors" data-id="element-400">
                {closeLabel}
              </button>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}